import React, { useState, useEffect } from 'react';
import { Board as BoardType } from '../services/api';
import { Board } from './Board';
import { Navbar } from './Navbar';
import { boardApi } from '../services/api';
import { useSearchParams, useParams } from 'react-router-dom';

export const BoardPage: React.FC = () => {
  const [boards, setBoards] = useState<BoardType[]>([]);
  const [currentBoard, setCurrentBoard] = useState<BoardType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const { boardId: boardIdParam } = useParams<{ boardId: string }>();

  useEffect(() => {
    loadBoards();
  }, [boardIdParam]);

  const loadBoards = async () => {
    try {
      setLoading(true);
      const response = await boardApi.getAll();
      const boardsData = response.data.boards || [];
      setBoards(boardsData);
      
      // Vérifier si un board spécifique est demandé dans l'URL
      // Priorité 1: boardId depuis le path (/boards/:boardId)
      // Priorité 2: board depuis les query params (/?board=id)
      const boardIdFromPath = boardIdParam ? parseInt(boardIdParam) : null;
      const boardIdFromQuery = searchParams.get('board') ? parseInt(searchParams.get('board')!) : null;
      const requestedBoardId = boardIdFromPath || boardIdFromQuery;
      
      if (requestedBoardId) {
        const requestedBoard = boardsData.find(b => b.id === requestedBoardId);
        if (requestedBoard) {
          setCurrentBoard(requestedBoard);
        } else if (boardsData.length > 0) {
          setCurrentBoard(boardsData[0]);
        }
      } else if (boardsData.length > 0 && !currentBoard) {
        setCurrentBoard(boardsData[0]);
      }
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des tableaux');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const refreshCurrentBoard = async () => {
    if (!currentBoard) return;
    
    try {
      const response = await boardApi.getOne(currentBoard.id);
      setCurrentBoard(response.data);
    } catch (err) {
      console.error('Erreur lors du rafraîchissement du tableau', err);
    }
  };

  const handleBoardUpdate = async (updatedBoard: BoardType) => {
    try {
      await boardApi.update(updatedBoard.id, { title: updatedBoard.title });
      await loadBoards();
    } catch (err) {
      setError('Erreur lors de la mise à jour du tableau');
      console.error(err);
    }
  };

  const handleCreateBoard = async () => {
    try {
      const response = await boardApi.create({ title: 'Nouveau Tableau' });
      setBoards([...boards, response.data]);
      setCurrentBoard(response.data);
    } catch (err) {
      setError('Erreur lors de la création du tableau');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-kanban-blue">
        <div className="text-white text-2xl">Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-kanban-blue">
        <div className="text-white text-xl">
          <p>{error}</p>
          <button 
            onClick={loadBoards}
            className="mt-4 px-4 py-2 bg-white text-kanban-blue rounded hover:bg-gray-100"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (boards.length === 0) {
    return (
      <>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen bg-kanban-blue">
          <div className="text-center">
            <p className="text-white text-xl mb-4">Aucun tableau disponible</p>
            <button 
              onClick={handleCreateBoard}
              className="px-6 py-3 bg-white text-kanban-blue rounded-lg hover:bg-gray-100 font-semibold"
            >
              Créer un tableau
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="App">
      {currentBoard && (
        <Board 
          board={currentBoard} 
          onUpdate={handleBoardUpdate}
          onRefresh={refreshCurrentBoard}
        />
      )}
    </div>
  );
};
